import React, { useEffect, useState } from "react";
import { navigate, useQueryParams } from "raviger";
import unlockImg from "../../../assets/img/verify-otp.svg";
import ezycompLogo from "../../../assets/img/logo.png";
import Navbar from "../../shared/Navbar";
import { useUnlockAccount } from "../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../shared/PageLoader";
import { ERROR_MESSAGES } from "../../../utils/constants";
import { getBasePath } from "../../../App";

function UnlockAccount() {
    const [query] = useQueryParams();
    const [unlockStatus, setUnlockStatus] = useState<'loading' | 'success' | 'error' | 'invalid'>('loading');
    const unlockToken = query && query.unlock_token;

    const { unlockAccount, isLoading } = useUnlockAccount(
        (response: any) => {
            if (response && response.data) {
                setUnlockStatus('success');
                toast.success("Account unlocked successfully! You can now log in.");
            } else {
                setUnlockStatus('error');
                const errorMessage = (response && response.data && response.data.error) || "Failed to unlock account";
                toast.error(errorMessage);
            }
        },
        (error: any) => {
            setUnlockStatus('error');
            const errorMessage = (error && error.response && error.response.data && error.response.data.error) || ERROR_MESSAGES.DEFAULT;
            toast.error(errorMessage);
        }
    );

    useEffect(() => {
        if (unlockToken) {
            unlockAccount({ unlock_token: unlockToken });
        } else {
            setUnlockStatus('invalid');
        }
    }, [unlockToken, unlockAccount]);

    function handleGoToLogin() {
        navigate(`${getBasePath()}/login`);
    }

    function renderContent() {
        switch (unlockStatus) {
            case 'loading':
                return (
                    <div className="text-center">
                        <img src={unlockImg} alt="UNLOCK ACCOUNT" height="200px" className="mb-4" />
                        <h3 className="text-muted">Unlocking your account...</h3>
                        <p className="text-muted">Please wait while we verify your unlock token.</p>
                    </div>
                );
            
            case 'success':
                return (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h3 className="text-success mb-3">Account Unlocked Successfully!</h3>
                        <p className="text-muted mb-4">
                            Your account has been unlocked and you can now log in with your credentials.
                        </p>
                        <button 
                            className="btn btn-primary px-4 py-2"
                            onClick={handleGoToLogin}
                        >
                            Go to Login
                        </button>
                    </div>
                );
            
            case 'error':
                return (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="fas fa-exclamation-circle text-danger" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h3 className="text-danger mb-3">Unlock Failed</h3>
                        <p className="text-muted mb-4">
                            We couldn't unlock your account. The unlock token may be invalid or expired. Please contact your system administrator for assistance.
                        </p>
                        <button 
                            className="btn btn-primary px-4 py-2"
                            onClick={handleGoToLogin}
                        >
                            Back to Login
                        </button>
                    </div>
                );
            
            case 'invalid':
                return (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h3 className="text-warning mb-3">Invalid Unlock Link</h3>
                        <p className="text-muted mb-4">
                            The unlock link is invalid or missing required parameters. Please contact your system administrator for assistance.
                        </p>
                        <button 
                            className="btn btn-primary px-4 py-2"
                            onClick={handleGoToLogin}
                        >
                            Go to Login
                        </button>
                    </div>
                );
            
            default:
                return null;
        }
    }

    return (
        <>
            <Navbar showUser={false} />
            <div className="page-layout-container bg-white">
                <div className="main-container overflow-hidden">
                    <div className="d-flex flex-row h-100 overflow-hidden justify-content-center my-4">
                        <div className="col-7" style={{ maxWidth: '700px' }}>
                            <div className="d-flex flex-row justify-content-center align-items-center mb-3">
                                <div className="text-xxxl fw-900 text-nowrap">Welcome to &nbsp;</div>
                                <div className="">
                                    <img src={ezycompLogo} alt="EZYCOMP LOGO" height={'100px'} />
                                </div>
                            </div>
                            <div className="card shadow px-3 pt-4 pb-5 mb-5 bg-body rounded">
                                <div className="p-0 text-center pt-2 mb-4">
                                    <h5 className="text-xl text-black-600">Unlock Account</h5>
                                </div>
                                <div className="d-flex flex-column justify-content-center mx-auto" style={{ minHeight: '200px' }}>
                                    {renderContent()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isLoading && (
                <PageLoader>
                    <p>Processing unlock request...</p>
                </PageLoader>
            )}
        </>
    );
}

export default UnlockAccount;